import { defineStore } from "pinia";
import _ from "lodash";
import * as UserService from "@/shared/api/userService";
import * as storeHelper from "@/shared/storeHelper";
import { FetchState, LoginMethod, LoginPromptOrchestratorState, LoginPromptToDisplay } from "@/shared/types";
import { useHsabAccountsStore } from "@/employee/store/hsabAccounts";
import { useCustodianSettingsStore } from "@/shared/store/custodianSettings";
import { useUserStore } from "@/shared/store/user";
import { useMfaStore } from "@/shared/store/mfa";

export const initialState: LoginPromptOrchestratorState = {
    acceptedTerms: [],
    acceptedTermsFetchState: FetchState.Uninitialized,
    promptToDisplay: LoginPromptToDisplay.None,
    isTermsSignatureRequired: false,
    isMfaPromptRequired: false,
    calculationsComplete: false,
};

export const useLoginPromptOrchestratorStore = defineStore("loginPromptOrchestrator", {
    state: () => _.cloneDeep(initialState),
    getters: {
        areAcceptedTermsLoading: state => state.acceptedTermsFetchState === FetchState.Loading,
        areAcceptedTermsLoaded: state => state.acceptedTermsFetchState === FetchState.Loaded,
        isIdentifierAccepted: state => (key: string) => state.acceptedTerms.some(x => x.termsIdentifier === key),
        showTermsPrompt: state => state.promptToDisplay === LoginPromptToDisplay.Terms,
        showMfaPrompt: state => state.promptToDisplay === LoginPromptToDisplay.Mfa,
    },
    actions: {
        async loadRequiredData () {
            const loadAccepted = this.loadAcceptedTermsForCurrentUser();
            // MFA data is loaded via login chain and may or may not be ready. There is a @Watch
            // in LoginPromptOrchestrator to handle if this data is not ready yet here.

            return Promise.all([loadAccepted]).then(_ => {
                this.calculateWhatToDisplay();
            });
        },
        async loadAcceptedTermsForCurrentUser (force = false) {
            if (this.acceptedTermsFetchState === FetchState.Loaded && !force) {
                return Promise.resolve();
            }

            this.acceptedTermsFetchState = FetchState.Loading;
            return UserService.GetAcceptedUserTermsAndConditions()
                .then(result => {
                    this.acceptedTerms = result.data;
                    this.acceptedTermsFetchState = FetchState.Loaded;
                })
                .catch(error => {
                    this.acceptedTermsFetchState = FetchState.Failed;
                    return storeHelper.handleError({ error, rethrow: true });
                });
        },
        async calculateWhatToDisplay () {
            const t1 = this.calculateTermsNeedSigning();
            const t2 = this.calculateShowMFAPrompt();

            return Promise.all([t1, t2]).then(_ => {
                this.calculationsComplete = true;
                this.showNextPrompt();
            });
        },
        calculateTermsNeedSigning (): Promise<void> {
            const hsabAccountsStore = useHsabAccountsStore();
            const custodianSettingsStore = useCustodianSettingsStore();

            const hsaTermsUri = custodianSettingsStore.hsaTermsUrlOrDefault;
            const eSignUri = custodianSettingsStore.eSignPdfUrlOrDefault;
            const termsUri = custodianSettingsStore.termsUrl;

            if (!this.isIdentifierAccepted(termsUri) || !this.isIdentifierAccepted(eSignUri)) {
                this.isTermsSignatureRequired = true;
            } else if (hsabAccountsStore.userHasHsaAccount && !this.isIdentifierAccepted(hsaTermsUri)) {
                this.isTermsSignatureRequired = true;
            }

            return Promise.resolve();
        },
        calculateShowMFAPrompt (): Promise<void> {
            const hsabAccountsStore = useHsabAccountsStore();
            const custodianSettingsStore = useCustodianSettingsStore();
            const userStore = useUserStore();
            const mfaStore = useMfaStore();

            this.isMfaPromptRequired = custodianSettingsStore.allowMfa
                && mfaStore.showMfaModal
                && userStore.loginMethod !== LoginMethod.Sso
                && hsabAccountsStore.userHasAnyActiveAccount;

            return Promise.resolve();
        },
        dismissTerms () {
            this.isTermsSignatureRequired = false;
            this.showNextPrompt();
        },
        dismissMfa () {
            this.isMfaPromptRequired = false;
            this.showNextPrompt();
        },
        showNextPrompt () {
            if (!this.calculationsComplete) {
                return;
            }

            if (this.isTermsSignatureRequired) {
                this.promptToDisplay = LoginPromptToDisplay.Terms;
            } else if (this.isMfaPromptRequired) {
                this.promptToDisplay = LoginPromptToDisplay.Mfa;
            } else {
                this.promptToDisplay = LoginPromptToDisplay.None;
            }
        }
    }
});